/* lib/format.js — RU-форматтеры: деньги, проценты, граммы, даты.
   ХЕНДОФФ: переносится в frontend/src/lib/format.js 1:1. */

  const nf = (opts) => new Intl.NumberFormat('ru-RU', opts);

  const moneyWith = (symbolOf) => (v, { sign = false } = {}) => {
    if (v == null || isNaN(v)) return '—';
    // Копейки показываем, когда они есть (561,60 ₽); целые — без «,00» (990 ₽).
    // Копейки считаем один раз (cents) и форматируем их же — иначе детекция и
    // Intl-округление расходятся на границах вида 8.995 («9,00 ₽» вместо «9 ₽»).
    const cents = Math.round(Math.abs(v) * 100);
    const digits = cents % 100 ? 2 : 0;
    const s = nf({ minimumFractionDigits: digits, maximumFractionDigits: digits }).format(cents / 100);
    const pre = v < 0 ? '−' : (sign && v > 0 ? '+' : '');
    // Однобуквенный символ (RUB/USD/EUR-знаки) клеим NBSP - не отрывается от числа.
    // Многобуквенный код (AED/UZS/...) - обычным пробелом: под давлением ширины
    // перенос происходит ЦЕЛИКОМ перед кодом, а не внутри него (AE|D);
    // цифры между собой связаны NBSP-разделителями Intl и не рвутся.
    const sym = symbolOf();
    const sep = sym.length > 1 ? ' ' : '\u00A0';
    return `${pre}${s}${sep}${sym}`;
  };

  const money = moneyWith(() => '₽');

  // ── Валюта отображения POS-данных (вкладки Аналитики) ────────────────────
  // Только символ, БЕЗ конвертаций. Код приходит с подключения
  // (/api/integrations/ → connection.currency); список зеркалит бэкенд
  // (integrations/views.py _DISPLAY_CURRENCIES) байт-в-байт.
  const CURRENCIES = [
    { code: 'RUB', symbol: '₽', label: '₽ — российский рубль' },
    { code: 'AED', symbol: 'AED', label: 'AED — дирхам ОАЭ' },
    { code: 'USD', symbol: '$', label: '$ — доллар США' },
    { code: 'EUR', symbol: '€', label: '€ — евро' },
    { code: 'GBP', symbol: '£', label: '£ — фунт стерлингов' },
    { code: 'KZT', symbol: '₸', label: '₸ — казахстанский тенге' },
    { code: 'BYN', symbol: 'Br', label: 'Br — белорусский рубль' },
    { code: 'UAH', symbol: '₴', label: '₴ — украинская гривна' },
    { code: 'TRY', symbol: '₺', label: '₺ — турецкая лира' },
    { code: 'CNY', symbol: '¥', label: '¥ — китайский юань' },
    { code: 'THB', symbol: '฿', label: '฿ — тайский бат' },
    { code: 'GEL', symbol: '₾', label: '₾ — грузинский лари' },
    { code: 'AMD', symbol: '֏', label: '֏ — армянский драм' },
    { code: 'UZS', symbol: 'UZS', label: 'UZS — узбекский сум' },
    { code: 'KGS', symbol: 'KGS', label: 'KGS — киргизский сом' },
    { code: 'AZN', symbol: '₼', label: '₼ — азербайджанский манат' },
    { code: 'RSD', symbol: 'RSD', label: 'RSD — сербский динар' },
  ];
  const CUR_BY_CODE = Object.fromEntries(CURRENCIES.map((c) => [c.code, c]));
  let _analyticsCur = 'RUB';
  const analyticsCurrency = {
    set(code) { _analyticsCur = CUR_BY_CODE[code] ? code : 'RUB'; },
    code() { return _analyticsCur; },
    symbol() { return CUR_BY_CODE[_analyticsCur].symbol; },
  };
  // Деньги для экранов АНАЛИТИКИ: символ валюты пространства, читается на
  // каждый вызов (контейнер Аналитики ставит его до рендера отчётов). Остальной
  // продукт (Кухня, биллинг) остаётся на fmt.money с ₽.
  const moneyA = moneyWith(() => analyticsCurrency.symbol());

  const percent = (v, { digits = 0, sign = false } = {}) => {
    if (v == null || isNaN(v)) return '—';
    const s = nf({ minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Math.abs(v));
    const pre = v < 0 ? '−' : (sign && v > 0 ? '+' : '');
    return `${pre}${s}\u00A0%`;
  };

  const grams = (v) => {
    if (v == null || isNaN(v)) return '—';
    if (v >= 1000) return `${nf({ maximumFractionDigits: 2 }).format(v / 1000)}\u00A0кг`;
    return `${nf({ maximumFractionDigits: 1 }).format(v)}\u00A0г`;
  };

  const kcal = (v) => (v == null || isNaN(v)) ? '—' : `${nf({ maximumFractionDigits: 0 }).format(v)}\u00A0ккал`;

  const MONTHS = ['янв','фев','мар','апр','мая','июн','июл','авг','сен','окт','ноя','дек'];
  /* `timeZone` — для дат, которые по смыслу привязаны к поясу платформы (срок
     доступа «до 31 декабря» хранится как 23:59:59 МСК): без него у зрителя
     восточнее Москвы такая дата уезжает на следующий день. Обычные даты
     (создано/записан) остаются в локальном поясе браузера. */
  const date = (d, { timeZone } = {}) => {
    const dt = (d instanceof Date) ? d : new Date(d);
    if (isNaN(dt)) return '—';
    if (timeZone) {
      const parts = new Intl.DateTimeFormat('ru-RU', { timeZone, day: 'numeric', month: 'numeric', year: 'numeric' })
        .formatToParts(dt).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
      return `${Number(parts.day)} ${MONTHS[Number(parts.month) - 1]} ${parts.year}`;
    }
    return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
  };
  const dateShort = (d) => {
    const dt = (d instanceof Date) ? d : new Date(d);
    if (isNaN(dt)) return '—';
    return `${dt.getDate()} ${MONTHS[dt.getMonth()]}`;
  };
  const relativeDay = (d) => {
    const dt = (d instanceof Date) ? d : new Date(d);
    if (isNaN(dt)) return '—';
    const today = new Date(); today.setHours(0,0,0,0);
    const t = new Date(dt); t.setHours(0,0,0,0);
    const diff = Math.round((today - t) / 86400000);
    if (diff === 0) return 'Сегодня';
    if (diff === 1) return 'Вчера';
    if (diff > 1 && diff < 7) return `${diff} дн. назад`;
    return dateShort(dt);
  };

  // склонение: plural(5, 'чат','чата','чатов')
  const plural = (n, one, few, many) => {
    const m10 = n % 10, m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return one;
    if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
    return many;
  };

  /* Разбор готовой денежной строки на число и знак валюты.
     Нужен вёрстке крупных сумм: «4 809 936,21 AED» рисуется как крупное число
     плюс приглушённый мелкий знак валюты. Это не косметика — на отчётах с
     миллионами код валюты занимает до пятой части ширины карточки, а читают
     в нём ровно один раз: какая валюта, и так уже понятно из шапки отчёта.
     Разделитель ставит сам форматтер (NBSP у односимвольных, пробел у кодов),
     поэтому режем по ПОСЛЕДНЕМУ пробелу любого вида и проверяем, что хвост —
     действительно знак валюты, а не часть значения («5 % плана», «10 поз.»). */
  const CUR_TOKENS = new Set(CURRENCIES.map((c) => c.symbol));
  const splitMoney = (s) => {
    const str = String(s == null ? '' : s);
    const i = Math.max(str.lastIndexOf(' '), str.lastIndexOf(' '));
    if (i <= 0) return { num: str, sep: '', cur: '' };
    const cur = str.slice(i + 1);
    // Разделитель возвращаем ОТДЕЛЬНЫМ полем, а не выбрасываем: он обязан попасть
    // в разметку, иначе выделение и копирование дают «4 819 375,31AED» — слипшуюся
    // строку, которую электронная таблица не разберёт как число с валютой. Отчёт,
    // из которого нельзя скопировать сумму, бесполезен ровно там, где им
    // пользуются чаще всего — при сверке с кассой и бухгалтерией.
    return CUR_TOKENS.has(cur)
      ? { num: str.slice(0, i), sep: str[i], cur }
      : { num: str, sep: '', cur: '' };
  };

  /* Подпись денежной оси графика. Ось узкая — на неё выделено порядка 60px, —
     а полная сумма туда не влезает и переносится: «240 000 / AED» в две строки,
     съедая ширину самого графика. Поэтому здесь и только здесь сумма СОКРАЩАЕТСЯ
     (в карточках и таблицах это запрещено: по ним сверяются с кассой, и точное
     значение обязано быть точным). Знак валюты на оси не повторяем — он один на
     весь отчёт и написан в шапке; тултип графика показывает полную сумму.
     Правило жило приватной функцией в «Обзоре», и четыре денежных графика из
     шести остались без него: тренд разреза, тренд закупок, тренд официантов и
     график внутри карточки официанта. */
  /* Тысячи до десяти — с десятыми («1,5K»): округление до целых писало деления
     1 500 и 2 000 одинаково «2K, 2K», и ось из повторов не читалась. Так выглядит
     выручка по часам у небольшого заведения и средний чек — их деления идут через
     500. От 10K деления крупнее тысячи, и десятые там — лишний шум. */
  const nfAxis = new Intl.NumberFormat('ru-RU');
  const moneyAxis = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1_000_000) return nfAxis.format(Math.round(n / 100_000) / 10) + 'M';
    if (Math.abs(n) >= 10_000) return nfAxis.format(Math.round(n / 1000)) + 'K';
    if (Math.abs(n) >= 1000) return nfAxis.format(Math.round(n / 100) / 10) + 'K';
    return String(Math.round(n));
  };

  /* Средние СЧЁТНЫХ величин: позиций, гостей, заказов в час (график и тепловая карта
     «Производительности»). Раньше их печатал Math.round, и 0,3 позиции в час выходило
     «0» — честно малый час читался как «продаж не было». Ноль на экране означает
     ровно ноль, поэтому точность растёт по мере того, как значение мельчает:
       ≥ 10 — целое; 1…10 — один знак; меньше 1 — до двух знаков («0,3», «0,05»);
       то, что и в двух знаках округлилось бы в ноль, — «<0,01».
     Ноль и пустое возвращаются как «0» и «—»: показать ли их вовсе, решает вызывающий
     (тепловая карта пишет «нет продаж»).
     Округляет сам Intl — по кратчайшей десятичной записи числа: 9,95 → «10» (toFixed
     округлил бы по двоичной, 9,9499…, и дал бы 9,9). Хвостовые нули не пишем: «2», а
     не «2,0», — поэтому и округление через границу ступени даёт «10», а не «10,0». */
  const nfAvg = [0, 1, 2].map((d) => nf({ minimumFractionDigits: 0, maximumFractionDigits: d }));
  const avg = (v) => {
    if (v == null || v === '' || isNaN(v)) return '—';
    const n = Number(v);
    if (n === 0) return '0';
    const a = Math.abs(n);
    if (a < 0.005) return n > 0 ? '<0,01' : '>−0,01';
    const d = a >= 10 ? 0 : (a >= 1 ? 1 : 2);
    return (n < 0 ? '−' : '') + nfAvg[d].format(a);
  };
  /* Ось тех же средних. Деления у recharts «круглые» (0,25 / 0,075 / 37,5), и подпись
     обязана их различать: округление до целого давало «0, 0, 1, 1, 1» — ось из
     повторов. Пишем деление как есть, до четырёх знаков (их хватает: сервер отдаёт
     средние с точностью до тысячных); четвёртый знак заодно гасит шум плавающей
     точки (0,30000000000000004 → «0,3»). */
  const nfAvgAxis = nf({ maximumFractionDigits: 4 });
  const avgAxis = (v) => {
    const n = Number(v);
    if (v == null || v === '' || !Number.isFinite(n)) return '';
    if (n === 0) return '0';
    return (n < 0 ? '−' : '') + nfAvgAxis.format(Math.abs(n));
  };

  window.fmt = { money, percent, grams, kcal, date, dateShort, relativeDay, plural, splitMoney, moneyAxis, avg, avgAxis };
  // fmtA — те же форматтеры для экранов Аналитики, но money — в валюте пространства.
  window.fmtA = { ...window.fmt, money: moneyA };

const __ex_fmt = window.fmt; export { __ex_fmt as fmt };
const __ex_fmtA = window.fmtA; export { __ex_fmtA as fmtA };
export { analyticsCurrency, CURRENCIES };
